import React, { useRef, useState } from "react";
import { FaPrint } from "react-icons/fa6";
import "./invoice-bill.style.scss";
import "../../components/form-inputs/form-inputs.style.scss";
import ReactToPrint from "react-to-print";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// components
import BillFilter from "../../components/bill-list/bill-filter.component";
import BillList from "../../components/bill-list/bill-list.component";
import StructuredSideModal from "../../components/structured-side-modal/structured-side-modal.component";
import BillAuditLogTemplate from "../../components/pdf-template/bill-audit-log.component";
import BillActionBar from "../../components/bill-list/bill-action-bar.component";
import CreditNoteForm from "../../components/bill-list/credit-note-form.component";
import BillSummaryCard from "../../components/bill-summary-card/bill-summary-card.component";
import type { SummaryStatusVariant } from "../../components/bill-summary-card/bill-summary-card.component";
import PrintOptionsDialog from "../../components/print-options-dialog/print-options-dialog.component";
import EmptyRecord from "../../components/empty-record/empty-record.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";

// form handle
import { useFormik } from "formik";
import * as yup from "yup";

// global function
import { isEmptyObject } from "../../global/function";
import {
	isUnsyncedBillsModalSnoozed,
	snoozeUnsyncedBillsModal,
} from "../../global/unsyncedBillsModalSnooze";

// queries
import {
	useIncrementPrintCount,
	useBillDates,
	usePrintBill,
	usePrintCreditNote,
	recordedByName,
} from "../../queries/bill.queries";
import type { Bill, PrintSize, BillSortOrder } from "../../queries/bill.queries";
import { useCompanyDetails, useActiveExternalApp } from "../../queries/company.queries";
import { useBillStatus } from "../../hooks/useBillStatus";
import type { BillStatus } from "../../hooks/useBillStatus";
import { useLastPrintSize } from "../../hooks/useLastPrintSize";
import { useLastBillViewMode } from "../../hooks/useLastBillViewMode";
import {
	initialBillFilterValues,
	transformBillFilters,
	statusFilterToParams,
} from "../../components/bill-list/bill-filter.types";
import type { BillStatusFilter } from "../../components/bill-list/bill-filter.types";
import {
	CUSTOMER_ID_VALIDATION_PATTERN,
	CUSTOMER_ID_ERROR_MESSAGE,
} from "../../global/customer-id.util";

const billFilterSchema = yup.object().shape({
	customer_id: yup
		.string()
		.matches(CUSTOMER_ID_VALIDATION_PATTERN, {
			message: CUSTOMER_ID_ERROR_MESSAGE,
			excludeEmptyString: true,
		}),
	buyer_name: yup.string(),
	invoice_number: yup
		.string()
		.matches(
			/^(?:\d{4}|[a-zA-Z]\d{4})$/,
			"Input must be 4 digits or 1 letter followed by 4 digits.",
		),
	buyer_pan: yup.number().typeError("Must be a number"),
	date_from: yup.string(),
	date_to: yup.string(),
	total_sales_min: yup.number().typeError("Must be a number"),
	total_sales_max: yup.number().typeError("Must be a number"),
	fiscal_year: yup
		.string()
		.matches(
			/^\d{4}\.\d{3}$/,
			"Enter a fiscal year in the format XXXX.XXX",
		),
});

type SelectedBillState = Bill | Record<string, never>;

// Only canceled/credit-issued map to BillSummaryCard's `status` badge —
// "ird-failed"/"normal" aren't SummaryStatusVariant values at all (IRD state
// is now its own irdBillStatus prop, passed separately in buildSummaryProps
// below), and a credit note being viewed is inherently a credit note (no
// badge needed).
function summaryStatusFor(
	status: BillStatus | null,
	asCreditNote: boolean,
): SummaryStatusVariant | undefined {
	if (asCreditNote) return undefined;
	if (status === "canceled" || status === "credit-issued") return status;
	return undefined;
}

export default function InvoiceBill() {
	const { data: companyData } = useCompanyDetails();
	const companyDetails = companyData?.company;
	const { billModel } = useActiveExternalApp();
	const billCreationBlocked = billModel === "A";

	// Same shared query top-navigation's unsynced-bill badge reads — dedupes
	// what used to be 4 separate getBillDates() mount-effect dispatches.
	const { data: billDates } = useBillDates();

	const [selectedBill, setSelectedBill] = useState<SelectedBillState>({});
	const [viewMode, setViewMode] = useLastBillViewMode();

	const [tempViewModal, setTempViewModal] = useState(false);
	const [isAddCreditNote, setIsAddCreditNote] = useState(false);
	const [isCreditNoteDirty, setIsCreditNoteDirty] = useState(false);
	const [viewCreditNote, setViewCreditNote] = useState(false);
	const [viewAuditLog, setViewAuditLog] = useState(false);
	const [printDialogOpen, setPrintDialogOpen] = useState(false);
	const [printSuccessNotice, setPrintSuccessNotice] = useState<string | null>(
		null,
	);

	const location = useLocation() as { state?: { customerId?: string } };
	const navigate = useNavigate();

	// ref for print of audit log template — audit log printing is unaffected
	// by the BE-PDF migration below, still uses react-to-print directly.
	const auditLogTemplateRef = useRef<HTMLDivElement>(null);

	const printMutation = useIncrementPrintCount();
	const [lastPrintSize, setLastPrintSize] = useLastPrintSize();
	const printBillMutation = usePrintBill();
	const printCreditNoteMutation = usePrintCreditNote();

	// A customerId arriving via navigation state (customer profile's "view
	// bills" link) seeds both the filter form and the initial search — every
	// other filter, including date range, starts empty (no default date-range
	// window is seeded anymore; the page loads unfiltered by default).
	const customerIdFromNav = location.state?.customerId
		? location.state.customerId.split("_")[1]
		: "";
	const initialFormValues = {
		...initialBillFilterValues,
		customer_id: customerIdFromNav,
	};

	const filterFormik = useFormik({
		validationSchema: billFilterSchema,
		initialValues: initialFormValues,
		onSubmit: (values) => {
			setActiveFilters(transformBillFilters(values));
		},
		// Formik's onReset receives the pre-reset values, not the freshly-reset
		// ones (see resetForm's `props.onReset(state.values, ...)` call) — so
		// this must re-derive from initialFormValues directly rather than the
		// `values` argument, or Reset would just re-apply whatever was typed.
		onReset: () => {
			setActiveFilters(transformBillFilters(initialFormValues));
		},
	});

	// The actually-applied search — only updates on Search/Reset, not on every
	// keystroke in filterFormik. Drives BillList/BillFilter/BillDuplicate's
	// queries directly, decoupled from lastBillDate entirely (previously the
	// bill list fetch was gated behind first fetching lastBillDate, solely to
	// compute the now-removed default date-range window).
	const [activeFilters, setActiveFilters] = useState(() =>
		transformBillFilters(initialFormValues),
	);

	// Which header stat pill is selected (bill-list.component.tsx) — kept
	// separate from activeFilters since it's applied immediately on click,
	// not gated behind the advanced panel's Search button.
	const [statusFilter, setStatusFilter] = useState<BillStatusFilter>("all");

	// Sort dropdown (bill-list.component.tsx's bill-date-info) — same
	// immediate-apply treatment as statusFilter, not gated behind Search.
	const [sortOrder, setSortOrder] = useState<BillSortOrder>("invoice_number_desc");

	// What actually drives the bill list query: the last-submitted advanced
	// filters plus whichever status pill is currently selected and the chosen
	// sort order.
	const billFilterValues = {
		...activeFilters,
		...statusFilterToParams(statusFilter),
		sort: sortOrder,
	};

	React.useEffect(() => {
		if (
			billDates &&
			billDates.failedBills > 0 &&
			!isUnsyncedBillsModalSnoozed()
		) {
			// Snoozed immediately (before the user picks a button) so a dismiss
			// via backdrop/escape still counts as "seen today" — otherwise it'd
			// pop right back on the next mount of this page.
			snoozeUnsyncedBillsModal();
			Swal.fire({
				title: "Fix unsynced bills?",
				text: "You have some unsynced bills that needs your attention.",
				showDenyButton: true,
				confirmButtonText: "Sure",
				denyButtonText: "Remind me tomorrow",
				customClass: {
					popup: "alert-modal",
					actions: "btn-container",
					confirmButton: "alert-btn btn-confirm",
					denyButton: "alert-btn btn-cancel",
				},
			}).then((click) => {
				if (click.isConfirmed) {
					navigate("/error-fix");
				}
			});
		}
	}, [billDates, navigate]);

	const billStatus = useBillStatus(selectedBill);

	const handleBillSelection = (bill: SelectedBillState) => {
		if (!isEmptyObject(bill)) {
			// Table view has no inline right-hand panel at all (not just
			// hidden below 1190px), so it always needs the modal too.
			if (window.innerWidth <= 1190 || viewMode === "table") {
				setTempViewModal(true);
			}
			const billRow = bill as Bill;
			// if bill is void (added to credit note) then billed items is used from creditnote
			billRow?.bill_void
				? setSelectedBill({
						...billRow,
						billed_items: billRow?.credit_note?.billed_items ?? [],
					})
				: setSelectedBill(billRow);
		} else {
			setSelectedBill({});
		}
	};

	const handleModalClose = () => {
		if (viewAuditLog) {
			if (window.innerWidth > 1190 && viewMode !== "table") {
				setTempViewModal(false);
				setIsAddCreditNote(false);
				setViewCreditNote(false);
			}
			setViewAuditLog(false);
		} else {
			setTempViewModal(false);
			setIsAddCreditNote(false);
			setViewCreditNote(false);
			setViewAuditLog(false);
			if (window.innerWidth <= 1190 || viewMode === "table") {
				setSelectedBill({});
			}
		}
	};

	const handleAddCreditNote = () => {
		setIsAddCreditNote(true);
		setTempViewModal(true);
	};

	const handleCreditNoteCancel = () => {
		if (window.innerWidth <= 1190 || viewMode === "table") {
			setIsAddCreditNote(false);
		} else {
			setIsAddCreditNote(false);
			setTempViewModal(false);
		}
	};

	const handleCreditNoteSuccess = () => {
		handleModalClose();
		setSelectedBill({});
	};

	const handleCreditNoteView = () => {
		setTempViewModal(true);
		setIsAddCreditNote(false);
		setViewCreditNote((prev) => !prev);
	};

	const handleAuditLogsView = () => {
		setTempViewModal(true);
		setViewAuditLog(true);
		setIsAddCreditNote(false);
		setViewCreditNote(false);
	};

	// increase print count of (bill/creditNote) after print or download
	const updatePrintCount = async (
		id: string,
		model_type: "bill" | "credit",
		print_type: "print" | "download",
	) => {
		try {
			await printMutation.mutateAsync({
				updates: [{ id, model_type, print_type }],
			});
			if (model_type === "credit") {
				setSelectedBill((prev) => {
					const bill = prev as Bill;
					return bill.credit_note
						? {
								...bill,
								credit_note: {
									...bill.credit_note,
									print_copies:
										bill.credit_note.print_copies + 1,
								},
							}
						: prev;
				});
			} else {
				setSelectedBill((prev) => {
					const bill = prev as Bill;
					return {
						...bill,
						print_copies: (bill.print_copies ?? 0) + 1,
					};
				});
			}
		} catch {
			// no error UI existed for this before either — print still happened
		}
	};

	// Whichever of bill/credit-note is currently on screen in the modal — the
	// panel never shows the credit note (asCreditNote is always false there,
	// same as the old renderInvoiceTemplate's callers).
	const isViewingCreditNote = (asCreditNote: boolean) => {
		const bill = selectedBill as Bill;
		return asCreditNote && !!bill?.bill_void;
	};

	const handlePrintConfirm = async (size: PrintSize) => {
		const bill = selectedBill as Bill;
		try {
			if (isViewingCreditNote(viewCreditNote)) {
				const creditNoteId = bill?.credit_note?.credit_note_id ?? "";
				await printCreditNoteMutation.mutateAsync({
					creditNoteId,
					size,
				});
				await updatePrintCount(creditNoteId, "credit", "print");
			} else {
				const billId = bill?.bill_id ?? "";
				await printBillMutation.mutateAsync({ billId, size });
				await updatePrintCount(billId, "bill", "print");
			}
			setLastPrintSize(size);
			setPrintDialogOpen(false);
			setPrintSuccessNotice("PDF opened in a new tab.");
		} catch {
			// PrintOptionsDialog's own FormError (bound to whichever mutation is
			// active) renders the message — dialog stays open so the user can retry.
		}
	};

	// Builds BillSummaryCard's props from whichever of bill/credit-note is
	// being displayed. Reads only already-computed, server-stored fields —
	// never recomputes tax/discount, so this can't drift from the real PDF.
	const buildSummaryProps = (asCreditNote: boolean) => {
		const bill = selectedBill as Bill;
		if (asCreditNote && bill.credit_note) {
			const creditNote = bill.credit_note;
			return {
				documentLabel: `Credit Note #${creditNote.credit_note_number}`,
				date: creditNote.credit_note_date,
				dateNepali: creditNote.credit_note_date_nepali,
				buyerName: creditNote.buyer_name,
				buyerPan: creditNote.buyer_pan,
				customerMobile:
					bill.customer_mobile ?? bill.customer_info?.customer_mobile,
				recordedBy: recordedByName(creditNote.company_user),
				printCopies: creditNote.print_copies,
				refInvoiceNumber: creditNote.ref_invoice_number,
				reasonForReturn: creditNote.reason_for_return,
				billedItems: creditNote.billed_items,
				subTotal: creditNote.sub_total,
				totalSales: creditNote.total_sales,
				// bill_meta/ird_bill live on the parent Bill row, not the
				// CreditNote — a credit note only ever exists once its bill
				// already synced successfully with IRD, but the external-app
				// sync badge is still meaningful here.
				irdBillStatus: bill.ird_bill,
				billMeta: bill.bill_meta,
			};
		}
		return {
			documentLabel: `Invoice #${bill.invoice_number}`,
			date: bill.invoice_date,
			dateNepali: bill.invoice_date_nepali,
			salesDate: bill.sales_date,
			buyerName: bill.buyer_name,
			buyerPan: bill.buyer_pan,
			customerMobile:
				bill.customer_mobile ?? bill.customer_info?.customer_mobile,
			paymentMode: bill.payment_mode,
			recordedBy: recordedByName(bill.company_user),
			printCopies: bill.print_copies,
			lastPrintDate: bill.lastPrintDate,
			billedItems: bill.billed_items,
			subTotal: bill.sub_total,
			totalSales: bill.total_sales,
			discount:
				(bill.non_taxable_discount ?? 0) + (bill.taxable_discount ?? 0),
			serviceCharge:
				(bill.non_taxable_service_charge ?? 0) +
				(bill.taxable_service_charge ?? 0),
			nonTaxableAmount: bill.non_taxable_amt,
			taxableAmount: bill.esf
				? bill.amount_for_esf
				: bill.taxable_sales_vat,
			vat: bill.vat,
			esf: bill.esf,
			status: summaryStatusFor(billStatus, asCreditNote),
			irdBillStatus: bill.ird_bill,
			billMeta: bill.bill_meta,
		};
	};

	const handleRetry = () => {
		if (companyDetails?.company_data_corrupt) return;
		navigate("/error-fix", {
			state: { billData: JSON.stringify(selectedBill) },
		});
	};

	function renderSideModalContent() {
		if (isAddCreditNote) {
			return (
				<CreditNoteForm
					selectedBill={selectedBill as Bill}
					companyDataCorrupt={companyDetails?.company_data_corrupt}
					onCancel={handleCreditNoteCancel}
					onSuccess={handleCreditNoteSuccess}
					onDirtyChange={setIsCreditNoteDirty}
				/>
			);
		}
		if (viewAuditLog) {
			return (
				<div className="sales-audit-container">
					<div className="sales-audit-container-inner">
						<div
							className="modal-template-wrapper"
							ref={auditLogTemplateRef}
						>
							<BillAuditLogTemplate
								selectedBill={
									{
										...selectedBill,
										...(selectedBill as Bill)
											?.customer_info,
									} as Bill
								}
							/>
						</div>
					</div>
				</div>
			);
		}
		if (billStatus === "ird-failed") {
			return (
				<div className="modal-template-wrapper">
					<div className="blurobj">
						<div className="blur-title">
							This bill has not synced with IRD servers
						</div>
						<div className="blur-text">
							Please click the <b>Retry Submission</b> button
							above.
						</div>
					</div>
					<BillSummaryCard {...buildSummaryProps(false)} />
				</div>
			);
		}
		// SideModal keeps this mounted even while hidden — billStatus is null
		// until a bill is actually selected, so buildSummaryProps has nothing
		// to read yet.
		if (!billStatus) {
			return null;
		}
		const asCreditNote = isViewingCreditNote(viewCreditNote);
		return (
			<div className="modal-template-wrapper">
				<BillSummaryCard {...buildSummaryProps(asCreditNote)} />
			</div>
		);
	}

	function renderSideModalHeaderButtons() {
		if (viewAuditLog) {
			return (
				<div className="action-container">
					<ReactToPrint
						trigger={() => (
							<div className="action-btn">
								<FaPrint />
								Print
							</div>
						)}
						content={() => auditLogTemplateRef.current}
					/>
				</div>
			);
		}
		if (isAddCreditNote || !billStatus) {
			return null;
		}
		return (
			<BillActionBar
				status={billStatus}
				context="modal"
				onAuditLogsView={handleAuditLogsView}
				onPrint={() => setPrintDialogOpen(true)}
				onRetry={handleRetry}
				companyDataCorrupt={companyDetails?.company_data_corrupt}
				billCreationBlocked={billCreationBlocked}
				onAddCreditNote={handleAddCreditNote}
				onViewCreditNote={handleCreditNoteView}
				viewingCreditNote={viewCreditNote}
			/>
		);
	}

	function renderBillView() {
		if (billStatus === "ird-failed") {
			return (
				<div className="bill-template-wrapper">
					<div className="bill-template-container active">
						<div className="blurobj">
							<div className="blur-title">
								This bill has not synced with IRD servers
							</div>
							<div className="blur-text">
								Please click the <b>Retry Submission</b> button
								above.
							</div>
						</div>
						<BillSummaryCard {...buildSummaryProps(false)} />
					</div>
				</div>
			);
		}
		return (
			<div className="bill-template-wrapper">
				<div className="bill-template-container active">
					<BillSummaryCard {...buildSummaryProps(false)} />
				</div>
			</div>
		);
	}

	return (
		<div className={`invoice-bill-wrapper ${viewMode === "table" ? "table-mode" : ""}`}>
			<div className="invoice-bill-container">
				<BillFilter
					filterFormik={filterFormik}
					activeFilters={billFilterValues}
					statusFilter={statusFilter}
				/>

				<BillList
					selectedBillId={(selectedBill as Bill).bill_id}
					onSelectBill={handleBillSelection}
					billFilterValues={billFilterValues}
					statusFilter={statusFilter}
					onStatusFilterChange={setStatusFilter}
					sortOrder={sortOrder}
					onSortOrderChange={setSortOrder}
					viewMode={viewMode}
					onViewModeChange={setViewMode}
				/>

				{viewMode === "card" && (
					<div className="bill-template-action-container">
						<div className="temp-section-header">
							<h2>Bill View</h2>
							{billStatus && (
								<BillActionBar
									status={billStatus}
									context="panel"
									onAuditLogsView={handleAuditLogsView}
									onPrint={() => setPrintDialogOpen(true)}
									onRetry={handleRetry}
									companyDataCorrupt={
										companyDetails?.company_data_corrupt
									}
									billCreationBlocked={billCreationBlocked}
									onAddCreditNote={handleAddCreditNote}
									onViewCreditNote={handleCreditNoteView}
								/>
							)}
						</div>
						<InlineNotice
							message={printSuccessNotice}
							type="success"
							autoHideMs={4000}
							onDismiss={() => setPrintSuccessNotice(null)}
						/>
						{billStatus ? (
							renderBillView()
						) : (
							<EmptyRecord caption="Select a bill to view details" />
						)}
					</div>
				)}
			</div>
			<StructuredSideModal
				open={tempViewModal}
				onOpenChange={(open) => { if (!open) handleModalClose(); }}
				title={viewAuditLog ? "Audit Log" : isAddCreditNote ? "Credit Note" : "Bill"}
				actions={renderSideModalHeaderButtons()}
				width={760}
				isDirty={isAddCreditNote && isCreditNoteDirty}
			>
				<div style={{ padding: "16px 24px" }}>
					<InlineNotice
						message={printSuccessNotice}
						type="success"
						autoHideMs={4000}
						onDismiss={() => setPrintSuccessNotice(null)}
					/>
					{renderSideModalContent()}
				</div>
			</StructuredSideModal>
			<PrintOptionsDialog
				open={printDialogOpen}
				onOpenChange={setPrintDialogOpen}
				initialSize={lastPrintSize}
				isPending={
					printBillMutation.isPending ||
					printCreditNoteMutation.isPending
				}
				isError={
					printBillMutation.isError || printCreditNoteMutation.isError
				}
				error={printBillMutation.error ?? printCreditNoteMutation.error}
				onConfirm={handlePrintConfirm}
			/>
		</div>
	);
}
